import type { TourStep } from './types';

export type TourId =
  | 'account_hub'
  | 'home'
  | 'finance_deposit'
  | 'finance_wallet'
  | 'wallet_topup'
  | 'wallet_payout'
  | 'games'
  | 'settings'
  | 'account_security'
  | 'animations'
  | 'transactions'
  | 'faq'
  | 'support';

export interface TourDefinition {
  id: TourId;
  version: string;
  welcomeTitle: string;
  welcomeDescription: string;
  steps: TourStep[];
}

export function getTourForPath(pathname: string): TourDefinition | null {
  if (pathname === '/' || pathname === '') return homeTour;

  // More specific wallet paths before the wallet hub.
  if (
    pathname === '/account/finance/wallet/deposit' ||
    pathname.startsWith('/account/wallet/deposit-checkout') ||
    pathname === '/account/wallet/add-funds'
  ) {
    return walletTopUpTour;
  }
  if (pathname === '/account/wallet/payout-request') return walletPayoutTour;
  if (pathname === '/account/finance/wallet') return walletTour;

  if (pathname === '/account/finance/deposit') return depositTour;
  if (pathname === '/account/games' || pathname === '/account/finance/withdrawals') return gamesTour;
  if (pathname === '/account/settings') return settingsTour;
  if (pathname === '/account/security' || pathname === '/account/change-password') return accountSecurityTour;
  if (pathname === '/account/animations') return animationsTour;
  if (pathname === '/account/transactions') return transactionsTour;
  if (pathname === '/faq') return faqTour;
  if (
    pathname === '/support' ||
    pathname.startsWith('/support/') ||
    pathname.startsWith('/account/support')
  ) {
    return supportTour;
  }
  return null;
}

/** First-login chrome tour (header / nav). Prefer this over page tours until completed. */
export function getPostLoginTour(): TourDefinition {
  return chromeTour;
}

const v1 = 'v1';
const v2 = 'v2';
/** Bumped when chrome targets/copy left the old Account Hub layout. */
const chromeVersion = 'v2';

/** Header / top-nav tour — matches real chrome (Home, Games, Wallet, Activity, …). */
const chromeTour: TourDefinition = {
  id: 'account_hub',
  version: chromeVersion,
  welcomeTitle: 'Welcome to KioskGaming',
  welcomeDescription: 'A quick tour of the top bar so you know where everything lives.',
  steps: [
    {
      id: 'home',
      targetTourId: 'post-login-tour.home',
      title: 'Home',
      description: 'Browse the game lobby and jump back here anytime.',
      placement: 'bottom',
    },
    {
      id: 'gameManagement',
      targetTourId: 'post-login-tour.game_management',
      title: 'Games',
      description: 'Link and manage your game accounts and in-game balances.',
      placement: 'bottom',
    },
    {
      id: 'wallet',
      targetTourId: 'post-login-tour.wallet',
      title: 'Wallet',
      description: 'Check wallet balance, add funds, and request payouts.',
      placement: 'bottom',
    },
    {
      id: 'transactions',
      targetTourId: 'post-login-tour.transactions',
      title: 'Activity',
      description: 'Review deposits, withdrawals, and transaction status.',
      placement: 'bottom',
    },
    {
      id: 'balance',
      targetTourId: 'post-login-tour.balance',
      title: 'Your balance',
      description: 'Your wallet balance stays visible here while you browse.',
      placement: 'bottom',
    },
    {
      id: 'deposit',
      targetTourId: 'post-login-tour.deposit',
      title: 'Add funds',
      description: 'Top up your wallet so you can deposit into games.',
      placement: 'bottom',
    },
    {
      id: 'notification',
      targetTourId: 'post-login-tour.notification',
      title: 'Notifications',
      description: 'Announcements and important updates show up here.',
      placement: 'bottom',
    },
    {
      id: 'language',
      targetTourId: 'post-login-tour.language',
      title: 'Language',
      description: 'Switch the app language anytime.',
      placement: 'bottom',
    },
    {
      id: 'settings',
      targetTourId: 'post-login-tour.settings',
      title: 'Account menu',
      description: 'Open settings, security, support, or log out from here.',
      placement: 'bottom',
    },
  ],
};

const homeTour: TourDefinition = {
  id: 'home',
  version: v1,
  welcomeTitle: 'Home lobby',
  welcomeDescription: 'This is where you browse games and jump back into ones you already play.',
  steps: [
    {
      id: 'continue',
      targetTourId: 'tour.home.continue',
      title: 'Continue playing',
      description: 'Games you already linked show up here for a quick return.',
      placement: 'bottom',
    },
    {
      id: 'filters',
      targetTourId: 'tour.home.filters',
      title: 'Filters',
      description: 'Narrow the lobby by category or tag.',
      placement: 'bottom',
    },
    {
      id: 'search',
      targetTourId: 'tour.home.search',
      title: 'Search',
      description: 'Find a game platform by name.',
      placement: 'bottom',
    },
    {
      id: 'grid',
      targetTourId: 'tour.home.grid',
      title: 'Game lobby',
      description: 'Tap a tile to open details, deposit, or play.',
      placement: 'top',
    },
  ],
};

/** Deposit to games wizard — covers all 3 steps (cart → payment → checkout). */
const depositTour: TourDefinition = {
  id: 'finance_deposit',
  version: 'v3',
  welcomeTitle: 'Add funds to games',
  welcomeDescription: 'A walkthrough of every step: cart, payment method, and checkout.',
  steps: [
    {
      id: 'stepper',
      targetTourId: 'tour.deposit.stepper',
      title: 'Steps',
      description: 'Follow Add funds → Payment method → Checkout.',
      placement: 'bottom',
      activate: { kind: 'deposit_wizard_step', step: 1 },
    },
    {
      id: 'cart',
      targetTourId: 'tour.deposit.cart',
      title: 'Your cart',
      description: 'Games you will fund appear here. Tap a row to set its amount.',
      placement: 'right',
      activate: { kind: 'deposit_wizard_step', step: 1 },
    },
    {
      id: 'addGame',
      targetTourId: 'tour.deposit.add_game',
      title: 'Add a game',
      description: 'Open the picker to add more games to this order.',
      placement: 'top',
      activate: { kind: 'deposit_wizard_step', step: 1 },
    },
    {
      id: 'amount',
      targetTourId: 'tour.deposit.amount_panel',
      title: 'Amount',
      description: 'Enter or pick a quick amount for the selected game.',
      placement: 'left',
      activate: { kind: 'deposit_wizard_step', step: 1 },
    },
    {
      id: 'cta',
      targetTourId: 'tour.deposit.cta',
      title: 'Continue',
      description: 'When amounts look good, continue to check your wallet balance.',
      placement: 'top',
      activate: { kind: 'deposit_wizard_step', step: 1 },
    },
    {
      id: 'funding',
      targetTourId: 'tour.deposit.funding',
      title: 'How to fund',
      description: 'Your wallet balance must cover the full order before checkout.',
      placement: 'bottom',
      activate: { kind: 'deposit_wizard_step', step: 2 },
    },
    {
      id: 'review',
      targetTourId: 'tour.deposit.review_cta',
      title: 'Review checkout',
      description: 'Continue to the final review before confirming.',
      placement: 'top',
      activate: { kind: 'deposit_wizard_step', step: 2 },
    },
    {
      id: 'checkoutGames',
      targetTourId: 'tour.deposit.checkout_games',
      title: 'Games to fund',
      description: 'Double-check which games and amounts will receive funds.',
      placement: 'right',
      activate: { kind: 'deposit_wizard_step', step: 3 },
    },
    {
      id: 'checkoutPayment',
      targetTourId: 'tour.deposit.checkout_payment',
      title: 'Payment summary',
      description: 'Confirm the wallet debit and total before submitting.',
      placement: 'left',
      activate: { kind: 'deposit_wizard_step', step: 3 },
    },
    {
      id: 'confirm',
      targetTourId: 'tour.deposit.confirm_cta',
      title: 'Confirm',
      description: 'Submit to transfer funds from your wallet to the selected games.',
      placement: 'top',
      activate: { kind: 'deposit_wizard_step', step: 3 },
    },
  ],
};

const walletTour: TourDefinition = {
  id: 'finance_wallet',
  version: v2,
  welcomeTitle: 'Wallet Guide',
  welcomeDescription: 'Manage wallet balance, game funds, and payouts from one place.',
  steps: [
    {
      id: 'balanceCard',
      targetTourId: 'tour.wallet.balance_card',
      title: 'Your portfolio',
      description: 'See wallet balance, reserved funds, and totals in games.',
      placement: 'bottom',
    },
    {
      id: 'deposit',
      targetTourId: 'tour.wallet.deposit_section',
      title: 'Add funds',
      description: 'Top up your wallet or send funds straight into games.',
      placement: 'top',
    },
    {
      id: 'payout',
      targetTourId: 'tour.wallet.payout_card',
      title: 'Payout',
      description: 'Request a payout from your spendable wallet balance.',
      placement: 'top',
    },
    {
      id: 'inGames',
      targetTourId: 'tour.wallet.in_games',
      title: 'In your games',
      description: 'Linked games and balances — withdraw back to wallet here.',
      placement: 'top',
    },
    {
      id: 'activity',
      targetTourId: 'tour.wallet.recent_activity',
      title: 'Recent activity',
      description: 'Latest wallet movements; open Activity for the full history.',
      placement: 'top',
    },
  ],
};

const walletTopUpTour: TourDefinition = {
  id: 'wallet_topup',
  version: v1,
  welcomeTitle: 'Wallet deposit',
  welcomeDescription: 'Add funds to your wallet in three steps: amount, method, checkout.',
  steps: [
    {
      id: 'amount',
      targetTourId: 'tour.wallet_deposit.amount',
      title: 'Amount',
      description: 'Type an amount or tap a suggested chip.',
      placement: 'bottom',
    },
    {
      id: 'methods',
      targetTourId: 'tour.wallet_deposit.methods',
      title: 'Payment method',
      description: 'Choose how you want to pay (crypto and other available methods).',
      placement: 'top',
    },
    {
      id: 'checkout',
      targetTourId: 'tour.wallet_deposit.checkout',
      title: 'Checkout',
      description: 'Review the total and pay to credit your wallet.',
      placement: 'top',
    },
  ],
};

const walletPayoutTour: TourDefinition = {
  id: 'wallet_payout',
  version: v1,
  welcomeTitle: 'Wallet payout',
  welcomeDescription: 'Cash out from your wallet: amount, method, then confirm.',
  steps: [
    {
      id: 'amount',
      targetTourId: 'tour.wallet_payout.amount',
      title: 'Amount',
      description: 'Enter how much to withdraw, or use Max / quick amounts.',
      placement: 'bottom',
    },
    {
      id: 'methods',
      targetTourId: 'tour.wallet_payout.methods',
      title: 'Payout method',
      description: 'Pick where the funds should go.',
      placement: 'top',
    },
    {
      id: 'checkout',
      targetTourId: 'tour.wallet_payout.checkout',
      title: 'Confirm',
      description: 'Review the request and submit your payout.',
      placement: 'top',
    },
  ],
};

const transactionsTour: TourDefinition = {
  id: 'transactions',
  version: v2,
  welcomeTitle: 'Activity Guide',
  welcomeDescription: 'Filter and review your deposit and withdrawal history.',
  steps: [
    {
      id: 'filters',
      targetTourId: 'tour.transactions.filters',
      title: 'Filters',
      description: 'Filter by funds in/out, pending, and payment status.',
      placement: 'bottom',
    },
    {
      id: 'list',
      targetTourId: 'tour.transactions.list',
      title: 'Transaction list',
      description: 'Tap a row to see details (or open a payment link if available).',
      placement: 'top',
    },
    {
      id: 'pagination',
      targetTourId: 'tour.transactions.pagination',
      title: 'Pagination',
      description: 'Move through pages when you have a long history.',
      placement: 'top',
    },
  ],
};

const gamesTour: TourDefinition = {
  id: 'games',
  version: 'v3',
  welcomeTitle: 'Games Guide',
  welcomeDescription: 'Filter your list, then use each card’s actions to play or move funds.',
  steps: [
    {
      id: 'filters',
      targetTourId: 'tour.games.filters',
      title: 'Filters & search',
      description: 'Switch My games / All games, sort by balance, and search by name.',
      placement: 'bottom',
    },
    {
      id: 'grid',
      targetTourId: 'tour.games.grid',
      title: 'Game list',
      description: 'Each card is a linked or available game account.',
      placement: 'top',
    },
    {
      id: 'play',
      targetTourId: 'tour.games.play',
      title: 'Play',
      description: 'Launch the game in the player when a play URL is available.',
      placement: 'top',
    },
    {
      id: 'funds',
      targetTourId: 'tour.games.add_funds',
      title: 'Add funds',
      description: 'Open deposit with this game pre-selected.',
      placement: 'top',
    },
    {
      id: 'withdraw',
      targetTourId: 'tour.games.withdraw',
      title: 'Withdraw to wallet',
      description: 'Pull in-game balance back to your wallet.',
      placement: 'top',
    },
  ],
};

const settingsTour: TourDefinition = {
  id: 'settings',
  version: v1,
  welcomeTitle: 'Settings Guide',
  welcomeDescription: 'Manage your credentials and personal information.',
  steps: [
    {
      id: 'credentials',
      targetTourId: 'tour.settings.credentials',
      title: 'Credentials',
      description: 'View your username and request new credentials if needed.',
      placement: 'bottom',
    },
    {
      id: 'contact',
      targetTourId: 'tour.settings.contact',
      title: 'Email & phone',
      description: 'Link your email/phone to secure your account.',
      placement: 'top',
    },
    {
      id: 'profile',
      targetTourId: 'tour.settings.profile',
      title: 'Profile',
      description: 'Edit your basic info and address details here.',
      placement: 'top',
    },
  ],
};

const accountSecurityTour: TourDefinition = {
  id: 'account_security',
  version: v1,
  welcomeTitle: 'Account security',
  welcomeDescription: 'Reissue login credentials and manage two-factor authentication.',
  steps: [
    {
      id: 'reissue',
      targetTourId: 'tour.security.reissue',
      title: 'New credentials',
      description: 'Request a new username and temporary password by SMS/email (limited).',
      placement: 'bottom',
    },
    {
      id: 'twoFactor',
      targetTourId: 'tour.security.two_factor',
      title: 'Two-factor auth',
      description: 'Enable or disable 2FA to protect sign-in.',
      placement: 'top',
    },
  ],
};

const animationsTour: TourDefinition = {
  id: 'animations',
  version: v1,
  welcomeTitle: 'Animation settings',
  welcomeDescription: 'Control motion across the app and per page.',
  steps: [
    {
      id: 'global',
      targetTourId: 'tour.animations.global',
      title: 'All pages',
      description: 'Master switch — turns motion off everywhere.',
      placement: 'bottom',
    },
    {
      id: 'perPage',
      targetTourId: 'tour.animations.per_page',
      title: 'Individual pages',
      description: 'Toggle animations for specific screens when the master switch is on.',
      placement: 'top',
    },
  ],
};

const faqTour: TourDefinition = {
  id: 'faq',
  version: v1,
  welcomeTitle: 'FAQ Guide',
  welcomeDescription: 'Search answers or browse by category.',
  steps: [
    {
      id: 'filters',
      targetTourId: 'tour.faq.filters',
      title: 'Search & category',
      description: 'Find topics quickly with search or a category filter.',
      placement: 'bottom',
    },
    {
      id: 'list',
      targetTourId: 'tour.faq.list',
      title: 'Answers',
      description: 'Tap a question to expand the answer.',
      placement: 'top',
    },
    {
      id: 'support',
      targetTourId: 'tour.faq.support_cta',
      title: 'Still need help?',
      description: 'Open Support to contact the team.',
      placement: 'top',
    },
  ],
};

const supportTour: TourDefinition = {
  id: 'support',
  version: v1,
  welcomeTitle: 'Support Guide',
  welcomeDescription: 'Submit a ticket when you need help from the team.',
  steps: [
    {
      id: 'form',
      targetTourId: 'tour.support.form',
      title: 'Ticket form',
      description: 'Fill in contact details, category, and your message.',
      placement: 'bottom',
    },
    {
      id: 'submit',
      targetTourId: 'tour.support.submit',
      title: 'Submit',
      description: 'Send the ticket when everything looks right.',
      placement: 'top',
    },
  ],
};
